import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { parse } from 'query-string';

import MerchantLayout from '../../../layout/MerchantLayout';
import { crmLogin } from '../../../services/c2dcrm';
import { useAuth } from '../../../hooks/useAuth';
import { MerchantLoginForm } from '../../../types/merchant';
import MerchantLoginEmailView from './MerchantLoginEmailView';
import MerchantLoginPasswordView from './MerchantLoginPasswordView';
import { ROUTES } from '../../../constants/route';
import { useSnackbar } from '../../Context/SnackbarContext';
import { isEmail } from '../../../utils/validations';
import { config } from '../../../configs/config';
import { UserRoles } from '../../../constants/role';

const useStyles = makeStyles({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emailWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '40px',
  },
});

type ErrorType = {
  email?: string;
  password?: string;
};

type Query = {
  redirectUri?: string;
};

const MerchantLogin: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [username, setUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [hatLoggedIn, setHatLoggedIn] = useState(false);
  const [errors, setErrors] = useState<ErrorType>();
  const { loginMerchant } = useAuth();
  const { openSnackbar } = useSnackbar();
  const location = useLocation();
  const { redirectUri } = parse(location.search) as Query;


  const handleSubmit = async (inputs: MerchantLoginForm) => {
    try {
      const res = await crmLogin(inputs);

      if (res?.data?.access_token) {
        if (redirectUri) {
          window.location.assign(`${redirectUri}?token=${res.data.access_token}`);
        } else {
          loginMerchant(res.data.access_token, rememberMe);
          history.replace(ROUTES.MERCHANT);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.data?.detail === 'LOGIN_BAD_CREDENTIALS')
        setErrors({ password: 'Invalid username / password combination.' });
      else {
        openSnackbar('Error: Something went wrong while trying to log in.');
      }
    }
  };

  const onNext = (email: string) => {
    if (!email || !isEmail(email)) {
      setErrors({ email: 'Must be a valid email.' });
      return;
    }

    setUsername(email);
    window.scrollTo(0, 0);
    setCurrentStep(2);
  };

  const onLogin = async (password: string) => {
    if (!password) {
      setErrors({ password: 'Please enter a password.' });
      return;
    }

    handleSubmit({
      username,
      password,
    });
  };

  const onForgotPassword = () => history.push(ROUTES.MERCHANT_RECOVER_PASSWORD);

  useEffect(() => {
    const userRole =
      localStorage.getItem(config.userRoleKey) || sessionStorage.getItem(config.userRoleKey);

    if (userRole === UserRoles.PDA) {
      setHatLoggedIn(true);
    } else {
      setHatLoggedIn(false);
    }
  }, [])

  return (
    <MerchantLayout>
      {hatLoggedIn &&
        <div>You are logged in using your PDA.  You must log out to continue.</div>
      }

      {currentStep === 2 && (
        <div className={classes.emailWrapper}>
          <Typography variant="h3" color="textPrimary">
            {username}
          </Typography>
        </div>
      )}

      <div className={classes.root}>
        {currentStep === 1 && (
          <MerchantLoginEmailView
            onNext={onNext}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            onForgotPassword={onForgotPassword}
            error={errors?.email}
            clearErrors={() => setErrors({ email: undefined })}
          />
        )}
        {currentStep === 2 && (
          <MerchantLoginPasswordView
            onLogin={onLogin}
            onForgotPassword={onForgotPassword}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            error={errors?.password}
            clearErrors={() => setErrors({ password: undefined })}
          />
        )}
      </div>
    </MerchantLayout>
  );
};

export default MerchantLogin;
